import React, { useEffect, useRef, useState } from 'react';
import { T, O, M, A } from 'TOMA';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { useForm, Controller } from 'react-hook-form';
import Button from '@mui/material/Button';
import s from './FormInformationQuestion.module.scss';
import useApiContext from '../../hooks/useApiContext';
import useModalContext from 'hooks/useModalContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import CreateIcon from '@mui/icons-material/Create';
import { boolean, number, string } from 'yup';

interface Props {
  id: number;
  answers: string[];
  question: string;
  required: boolean;
  eventId: string | undefined;
  onDelete?: (questionId: number) => void;
  canEdit?: boolean;
  defaultOpen?: boolean;
  card?: boolean;
  // lets the parent page persist this question (e.g. from the page-level Save)
  registerSave?: (save: (() => Promise<void>) | null) => void;
}

interface FormValues {
  answers: string[];
  question: string;
  required: boolean;
  eventId: string | undefined;
  onDelete: () => any;
}

function FormInformationQuestion({
  id,
  question,
  answers,
  required,
  eventId,
  onDelete,
  canEdit,
  defaultOpen = false,
  card = false,
  registerSave,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleted, setDeleted] = useState(false);
  const [qId, setQID] = useState(id);
  const [isRequired, setIsRequired] = useState(required);
  const { eventRepository, userRepository } = useApiContext();
  const { setModalContent, setModalVisible } = useModalContext();
  const { control, handleSubmit, setValue, getValues, watch } =
    useForm<FormValues>({
      defaultValues: {
        question,
        answers,
        eventId,
      },
    });
  const currentAnswers = watch('answers') ?? [];
  const addAnswer = (): void =>
    setValue('answers', [...(getValues('answers') ?? []), '']);
  const removeAnswer = (index: number): void =>
    setValue(
      'answers',
      (getValues('answers') ?? []).filter((_, i) => i !== index)
    );
  const snapshot = (): string => {
    const { question: q, answers: a } = getValues();
    return JSON.stringify([q ?? '', a ?? []]);
  };
  const savedSnapshot = useRef(JSON.stringify([question ?? '', answers ?? []]));
  const saveRef = useRef<() => Promise<void>>(async () => undefined);
  saveRef.current = async () => {
    const values = getValues();
    const isNew = qId === 0;
    if (isNew ? !values.question?.trim() : snapshot() === savedSnapshot.current)
      return;
    const saved = await eventRepository.updateFormAnswers(qId, values);
    if (isNew) setQID(saved.id);
    savedSnapshot.current = snapshot();
  };
  useEffect(() => {
    registerSave?.(async () => await saveRef.current());
    return () => registerSave?.(null);
  }, []);
  const [isVisible, setIsVisible] = useState(defaultOpen);
  const onSubmit = (data: FormValues): any => {
    eventRepository
      .updateFormAnswers(qId, data)
      .then((e) => {
        if (qId === 0) {
          setQID(e.id);
        }
        savedSnapshot.current = snapshot();
        setIsVisible(false);
        toast(t('home:formInformation.saved'), { type: 'success' });
      })
      .catch((err: Error) => {
        toast(err.message, { type: 'error' });
      });
  };
  const user = userRepository.getUser();
  const editable = canEdit ?? user?.role === 'ADMIN';
  const toggleVisibility = (): any => {
    setIsVisible(!isVisible); // Toggle the visibility state
  };
  return (
    <T.Block
      className={card ? s.card : undefined}
      style={{ display: deleted ? 'none' : 'block' }}
    >
      <T.Flex
        valign={card ? 'center' : 'baseline'}
        halign="space-between"
        gap="2"
        className={card ? s.cardHeader : undefined}
      >
        <T.Flex
          mt={card ? '0' : '20'}
          valign={card ? 'center' : undefined}
          className={card ? s.cardLeft : undefined}
        >
          <T.Block>
            <button
              type="button"
              className={s.buttonQuestion}
              onClick={toggleVisibility}
            >
              {isVisible ? <CloseIcon /> : <CreateIcon />}
            </button>
          </T.Block>
          <T.Block
            w={card ? undefined : 'small'}
            className={card ? s.cardTitle : undefined}
          >
            <A.Text fontSize={card ? '18' : '24'}>
              {getValues().question}
            </A.Text>
          </T.Block>
          <T.Block>
            <A.Text mt="3">{t('home:formInformation.required_answer')}</A.Text>
          </T.Block>
          <T.Block ml="10">
            <A.InputSwitch
              disabled={!editable}
              checked={isRequired}
              onChange={() => {
                setIsRequired(!isRequired);
                setValue('required', !isRequired);
                handleSubmit(() => onSubmit(getValues()))();
              }}
            />
          </T.Block>
        </T.Flex>

        <button
          type="button"
          className={s.button}
          disabled={!editable}
          onClick={() => {
            setModalContent(
              <div>
                <A.Text fontSize="22" fw="bold" mb="20">
                  {t('home:formInformation.are_you_sure')}
                </A.Text>
                <T.Flex gap={2} valign="center" mb="-15">
                  <A.Button
                    bg="alert"
                    onClick={() => {
                      if (qId !== 0) {
                        eventRepository.removeQuestion(qId).then(() => {
                          setModalVisible(false);
                          setDeleted(true);
                          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        });
                      } else {
                        if (onDelete) {
                          onDelete(qId);
                          setModalVisible(false);
                        }
                      }
                    }}
                  >
                    {t('home:formInformation.confirm')}
                  </A.Button>
                  <A.Button
                    appearance="link"
                    color="grey-80"
                    mb="15"
                    textDecoration="underline"
                    onClick={() => setModalVisible(false)}
                  >
                    {t('home:formInformation.cancel')}
                  </A.Button>
                </T.Flex>
              </div>
            );
          }}
        >
          <A.Icon icon="trash" fontSize="18" color="alert"></A.Icon>
        </button>
      </T.Flex>
      <Box
        component="form"
        onSubmit={(e: React.FormEvent) => {
          // never let the browser submit/reload the page (Enter key, nested forms)
          e.preventDefault();
          e.stopPropagation();
          void handleSubmit(onSubmit)();
        }}
        sx={{ '& .MuiTextField-root': { m: 1 } }}
        className={isVisible ? s.visibleContent : s.hiddenContent}
        noValidate
      >
        <div>
          <Controller
            name="question"
            control={control}
            defaultValue={question} // Set default value
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                id="question"
                label="Question" // Internationalization example
                variant="outlined"
                disabled={!editable}
                fullWidth
                error={!!error} // Display error state if there is an error
                helperText={error ? error.message : ''} // Display error message if available
              />
            )}
          />
          {currentAnswers.map((item, index) => (
            <T.Flex key={index}>
              <Controller
                name={`answers.${index}`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id={`answer-${index}`}
                    disabled={!editable}
                    label={`${t('home:formInformation.answer')} ${index + 1}`}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
              {editable && currentAnswers.length > 1 && (
                <button
                  type="button"
                  className={s.button}
                  onClick={() => removeAnswer(index)}
                  aria-label="Remove answer"
                >
                  <A.Icon icon="trash" fontSize="18" color="alert"></A.Icon>
                </button>
              )}
            </T.Flex>
          ))}
          <Button
            type="button"
            onClick={addAnswer}
            disabled={!editable}
            size="small"
            sx={{ m: 1 }}
          >
            + {t('home:formInformation.add_answer', 'Add Answer')}
          </Button>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Button
            type="button"
            onClick={() => {
              void handleSubmit(onSubmit)();
            }}
            fullWidth
            variant="outlined"
            size="medium"
            disabled={!editable}
          >
            {t('home:formInformation.save')}
          </Button>
        </div>
      </Box>
    </T.Block>
  );
}

export default FormInformationQuestion;
